import React, { useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';

import { logger } from '@/utils/logger';

interface CheckInDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateVersion: (comment: string) => Promise<void>;
}

/**
 * Dialog to create a new version (check-in)
 */
export const CheckInDialog: React.FC<CheckInDialogProps> = ({ open, onClose, onCreateVersion }) => {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreate = async () => {
    try {
      setIsSubmitting(true);
      await onCreateVersion(comment);
      setComment('');
      onClose();
    } catch (error) {
      logger.error('Failed to create version:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Version</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Add a comment to describe the changes in this version (optional).
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          multiline
          rows={4}
          label="Version Comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Describe what changed in this version..."
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleCreate} variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Creating...' : 'Create Version'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
